import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {SupabaseService} from '../core/supabase.service';
import {AuthService} from '../core/auth/auth.service';
import {DialogChooseAgencyComponent} from '../components/dialogs/organization/dialog-choose-agency/dialog-choose-agency.component';
import {Agency} from '../models/interfaces';
import {Tables} from '../models/enums';

/**
 * Demande son agence à un collaborateur qui n'en a aucune dans l'organisation
 * courante.
 *
 * Le rattachement n'est plus une colonne du profil mais une ligne de
 * `profiles_organisations_agencies` : un collaborateur peut appartenir à
 * plusieurs agences d'une même organisation, toutes égales. Cet écran
 * d'amorçage n'en demande qu'une — les suivantes s'ajoutent depuis
 * l'administration.
 */
@Injectable({
  providedIn: 'root'
})
export class AgencySelectionService {
  constructor(
    private dialog: MatDialog,
    private supabaseService: SupabaseService,
    private authService: AuthService
  ) {
  }

  async checkAndPromptAgencySelection(): Promise<void> {
    const profile = this.authService.profile;
    const hasNoAgency = (profile?.agencies?.length ?? 0) === 0;

    if (!profile || !hasNoAgency || !profile.organisation_id) return;

    const {data} = await this.supabaseService.client
      .from(Tables.Agencies)
      .select('*')
      .eq('organisation_id', profile.organisation_id);

    if (!data || !data.length) return;

    const dialog = this.dialog.open(DialogChooseAgencyComponent, {data, maxWidth: '500px'});
    dialog.afterClosed().subscribe((result: Agency) => {
      // Re-vérifié à la fermeture : l'agence a pu être attribuée entre-temps
      // depuis l'administration.
      if (result && (this.authService.profile?.agencies?.length ?? 0) === 0) {
        this.attachAgency(result);
      }
    });
  }

  private attachAgency(agency: Agency): void {
    const membershipId = this.authService.profileOrganisationId;
    const organisationId = this.authService.profile?.organisation_id;

    if (!membershipId || !organisationId) {
      console.error('Rattachement d\'agence impossible : aucune appartenance active chargée.');
      return;
    }

    this.supabaseService.client
      .from(Tables.ProfilesOrganisationsAgencies)
      .insert({
        profile_organisation_id: membershipId,
        agency_id: agency.id,
        // Dénormalisé côté base pour que les deux clés étrangères composites
        // puissent vérifier que l'agence et l'appartenance sont bien du même
        // client : une agence d'une autre organisation est refusée par Postgres.
        organisation_id: organisationId,
      })
      .then(response => {
        if (response.error) {
          console.error('Rattachement d\'agence refusé', response.error);
          return;
        }
        if (this.authService.profile) {
          this.authService.profile = {
            ...this.authService.profile,
            agencies: [...(this.authService.profile.agencies ?? []), agency],
          };
        }
      });
  }
}
